// @ts-check
// =============================================================================
// js/app/boot.js — the app bootstrap. main.js calls startApp() and that's it.
//
// BOOT ORDER (plan.md Phase 0)
//   1. Build the app skeleton: nav mount + screen container inside #app.
//   2. Register every routed screen.
//   3. Kick off the online-image list fetch (non-blocking).
//   4. Show the SPLASH: 18+ gate on first visit, then login resolution
//      (stored reference → silent login; nothing → guest auto-login).
//   5. When the session becomes ready, drop the boot loader and route.
//
// The splash owns steps 4–5's UI; this file owns the wiring.
// =============================================================================

import { store, subscribe } from '../core/store.js';
import { registerRoute, startRouter, navigate, currentPath } from '../core/router.js';
import { loadOnlineImageList } from '../render/image-cache.js';
import { ensureSession } from './session.js';
import { initAnalytics } from './analytics.js';
import { initDebug } from './debug.js';
import { isQuickplay, startQuickplayDuel } from './quickplay.js';
import { loadBots } from '../net/bot-runner.js';
import { registerAssetWorker } from '../net/startup-bundle.js';
import { mountNavShell } from '../ui/nav-shell.js';

import * as splash from '../screens/splash.js';
import * as lobby from '../screens/lobby.js';
import * as gallery from '../screens/gallery.js';
import * as deckBuilder from '../screens/deck-builder.js';
import * as settings from '../screens/settings.js';
import * as rewards from '../screens/rewards.js';
import * as challengeLadder from '../screens/challenge-ladder.js';
import * as help from '../screens/help.js';
import * as duel from '../screens/duel/duel.js';

/**
 * Boot the whole client. Called exactly once, from main.js.
 * @returns {void}
 */
export function startApp() {
	const app = document.getElementById('app');
	if (!app) {
		console.error('BOOT FAILED: #app missing from the shell');
		return;
	}

	// --- 1. Skeleton: [nav][screen container] -------------------------------
	const navMount = document.createElement('div');
	navMount.id = 'nav';
	const screenEl = document.createElement('div');
	screenEl.id = 'screen';
	app.append(navMount, screenEl);

	// --- 2. Routes -----------------------------------------------------------
	registerRoute({ path: 'splash', render: splash.render, destroy: splash.destroy });
	registerRoute({ path: 'lobby', render: lobby.render, destroy: lobby.destroy });
	registerRoute({ path: 'gallery', render: gallery.render, destroy: gallery.destroy });
	registerRoute({ path: 'deck/:index', render: deckBuilder.render, destroy: deckBuilder.destroy });
	registerRoute({ path: 'settings', render: settings.render, destroy: settings.destroy });
	registerRoute({ path: 'rewards', render: rewards.render, destroy: rewards.destroy });
	registerRoute({ path: 'challenge', render: challengeLadder.render, destroy: challengeLadder.destroy });
	registerRoute({ path: 'help', render: help.render, destroy: help.destroy });
	registerRoute({ path: 'duel', render: duel.render, destroy: duel.destroy });

	// --- 3. Non-blocking startup fetches -------------------------------------
	loadOnlineImageList();
	initAnalytics();
	initDebug();
	// Route every duel/leader/gui asset through the one Startup.zip download
	// (serves DOM <img> too; the duel additionally seeds PIXI in warmDuelAssets).
	registerAssetWorker();
	// Bot brains load at boot (they're a few small scripts) so a bot duel
	// never waits on them — their deferred self-registration finishes long
	// before anyone reaches the ladder.
	void loadBots();

	// --- 4. Splash first (it owns the 18+ gate + login UX) -------------------
	// The router isn't started yet — splash renders directly so a deep link
	// (#/gallery) can't skip the legal gate.
	splash.render(screenEl, {});

	// --- 5. A NEW game pulls you in (host-waiting, join, spectate, bot start,
	// reconnect replay). Updates to a duel you've ALREADY seen don't — you may
	// have stepped out to the lobby/settings on purpose (the duel menu's
	// "Return to Lobby"); rejoin via the nav's Game button or the lobby's
	// Load row.
	let lastSeenDuelId = null;
	subscribe('duel', (duel) => {
		if (!duel) {
			lastSeenDuelId = null;
			return;
		}
		const isNew = duel.id !== lastSeenDuelId;
		lastSeenDuelId = duel.id;
		if (isNew && !duel.GameOver && currentPath() !== 'duel' && store.session.phase === 'ready') {
			navigate('duel');
		}
	});

	// --- 6. When login resolves, start routing -------------------------------
	const stop = subscribe('session', (session) => {
		if (session.phase !== 'ready') return;
		stop();

		// QUICKPLAY (served at /quickplay): same loading + 18+ gate as always,
		// but session-ready goes straight into the John vs John bot duel
		// instead of the lobby. The router starts ON the splash route (which
		// shows "Starting your game" in this mode) so nothing else flashes
		// while the game request flies; the duel watcher above routes into
		// #/duel the moment the duel lands. A failed start falls through to
		// the normal lobby (startQuickplayDuel already toasted why).
		if (isQuickplay()) {
			splash.destroy();
			removeBootLoader();
			mountNavShell(navMount);
			navigate('splash'); // pin the hash: an empty hash would render the lobby
			startRouter(screenEl);
			void startQuickplayDuel().then((started) => {
				if (!started) navigate('lobby');
			});
			return;
		}

		splash.destroy();
		removeBootLoader();
		mountNavShell(navMount);

		// Warm the duel's heavy chrome (card/board frame textures + the
		// battlefield frame art) now, in the background, so the FIRST duel mounts
		// already-assembled instead of downloading it live. Runs when the browser
		// is idle so it never competes with the lobby's own paint.
		const warm = () => void duel.warmDuelAssets();
		if (typeof requestIdleCallback === 'function') {
			requestIdleCallback(warm, { timeout: 3000 });
		} else {
			setTimeout(warm, 1200);
		}

		// Deep links: honor a meaningful hash; default into the lobby hub.
		// "?intro" deep-links into the guide (old client URLCODE === "intro").
		if (window.location.search.substring(1) === 'intro') {
			navigate('help');
		} else if (currentPath() === 'splash') {
			navigate('lobby');
		}
		startRouter(screenEl);
	});

	// The splash is interactive as soon as it renders — loader can go now.
	removeBootLoader();
}

/** Fade out + remove the inline boot loader from index.template.html. */
function removeBootLoader() {
	const loader = document.getElementById('boot-loader');
	if (!loader) return;
	loader.style.opacity = '0';
	setTimeout(() => loader.remove(), 400);
}
